import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PenNibIcon as PenNib, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

/**
 * Dashboard banner that surfaces when ANY contract is awaiting the
 * calling user's signature — either as breeder (status=draft) or
 * buyer (status=breeder_signed). Single CTA deep-links to
 * /contracts where the user picks which row to open.
 *
 * Hides itself silently when nothing's pending. Cheap on cold-start —
 * one GET /contracts/mine call piggy-backed off the existing endpoint.
 */
export default function ContractsAwaitingBanner() {
  const [count, setCount] = useState(0);
  const [role, setRole] = useState(null); // 'breeder' | 'buyer' | 'both'

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/contracts/mine", { silent: true }).catch(() => ({ data: [] })),
      api.get("/auth/me", { silent: true }).catch(() => null),
    ]).then(([c, u]) => {
      if (cancelled) return;
      const rows = Array.isArray(c?.data) ? c.data : [];
      const myUserId = u?.data?.id;
      const myEmail = (u?.data?.email || "").toLowerCase();
      let breederPending = 0, buyerPending = 0;
      for (const r of rows) {
        if (r.owner_user_id === myUserId && r.status === "draft") breederPending++;
        else if ((r.buyer_user_id === myUserId
          || (r.buyer_email || "").toLowerCase() === myEmail)
          && r.status === "breeder_signed") buyerPending++;
      }
      const total = breederPending + buyerPending;
      setCount(total);
      if (breederPending && buyerPending) setRole("both");
      else if (buyerPending) setRole("buyer");
      else if (breederPending) setRole("breeder");
    });
    return () => { cancelled = true; };
  }, []);

  if (!count) return null;

  const tone = role === "buyer"
    ? "border-crimson/40 bg-[#FFF0F0]"
    : "border-[#E8D9A6] bg-[#FBF7EC]";
  const copy = role === "buyer"
    ? <><strong>{count}</strong> {count === 1 ? "contract is" : "contracts are"} ready for your signature. The breeder has signed and is waiting on you.</>
    : role === "breeder"
    ? <><strong>{count}</strong> draft {count === 1 ? "contract is" : "contracts are"} ready for you to sign as breeder, then send to the buyer.</>
    : <><strong>{count}</strong> {count === 1 ? "contract needs" : "contracts need"} your attention — some draft, some awaiting your buyer signature.</>;

  return (
    <div
      className={`mb-6 rounded-xl border-2 ${tone} p-4 sm:p-5 flex items-start gap-3`}
      data-testid="contracts-awaiting-banner"
    >
      <span className="p-2 rounded-full bg-white shrink-0">
        <PenNib size={20} weight="duotone" className="text-crimson" />
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="font-spectral text-[17px] sm:text-lg leading-tight">
          {role === "buyer" ? "Action needed: sign your puppy contract" : "Contracts awaiting your signature"}
        </h3>
        <p className="text-[12.5px] sm:text-[13px] text-[#3F3F3F] mt-1 leading-snug">{copy}</p>
        <Link to="/contracts" data-testid="contracts-awaiting-cta" className="inline-block w-full sm:w-auto">
          <Button className="mt-3 bg-ink hover:bg-crimson text-white rounded-full h-10 px-5 w-full sm:w-auto">
            Open contracts <ArrowRight size={13} weight="bold" className="ml-1" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
